<div class="organisation-details-page">
  <!-- Loading State -->
  @if (loading) {
    <div class="d-flex flex-column align-items-center py-20">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ 'organisationDetails.loading' | translate }}</span>
      </div>
      <p class="text-muted mt-5">{{ 'organisationDetails.loading' | translate }}</p>
    </div>
  }

  @if (!loading && company) {
    <div class="container-xxl py-10">
      <!-- Header Banner -->
      @if (company.header_image_url) {
        <div class="header-banner mb-6">
          <img [src]="company.header_image_url" class="banner-image" [alt]="company.company_name"/>
        </div>
      }
      <!-- Organisation Header -->
      <div class="card mb-6">
        <div class="card-body">
          <div class="row g-5">
            <!-- Logo -->
            <div class="col-auto">
              <div class="organisation-logo-large">
                @if (company.company_logo_url) {
                  <img [src]="company.company_logo_url" [alt]="company.company_name"/>
                } @else {
                  <div class="logo-placeholder-large">
                    <span>{{ getCompanyInitials() }}</span>
                  </div>
                }
              </div>
            </div>

            <!-- Info -->
            <div class="col">
              <h1 class="fw-bold mb-2">{{ company.company_name }}</h1>
              @if (company.city || company.country) {
                <p class="text-muted mb-3">
                  <i class="fa fa-location-dot me-2"></i>
                  {{ company.city }}{{ company.city && company.country ? ', ' : '' }}{{ company.country }}
                </p>
              }
              <!-- Contact Actions -->
              <div class="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div class="d-flex flex-wrap gap-2">
                  @if (company.email) {
                    <a [href]="'mailto:' + company.email" class="btn btn-sm btn-light-primary">
                      <i class="fa fa-envelope me-2"></i>{{ company.email }}
                    </a>
                  }
                  @if (company.phone) {
                    <a [href]="'tel:' + company.phone" class="btn btn-sm btn-light-success">
                      <i class="fa fa-phone me-2"></i>{{ company.phone }}
                    </a>
                  }
                  @if (company.address) {
                    <a [href]="getGoogleMapsUrl()" target="_blank" class="btn btn-sm btn-light-info">
                      <i class="fa fa-map-marker-alt me-2"></i>{{ 'organisationDetails.viewOnMap' | translate }}
                    </a>
                  }
                </div>
                <div class="d-flex flex-column gap-2 align-items-stretch">
                  @if (showBecomeMemberButton) {
                    <app-button
                      [variant]="'primary'"
                      [size]="'sm'"
                      [label]="'organisationDetails.becomeMember' | translate"
                      [iconClass]="'fa-solid fa-id-card'"
                      [width]="'100%'"
                      (clicked)="onBecomeMember()"
                    />
                  }
                  @if (isLoggedIn) {
                    <div class="d-flex gap-2">
                      <app-button
                        [variant]="isFavorite ? 'danger' : 'neutral'"
                        [size]="'sm'"
                        [iconClass]="isFavorite ? 'fa-solid fa-heart' : 'fa-regular fa-heart'"
                        [loading]="favoriteLoading"
                        (clicked)="toggleFavorite()"
                      />
                      <app-button
                        [variant]="'primary'"
                        [size]="'sm'"
                        [label]="'organisationDetails.message' | translate"
                        [iconClass]="'fa-solid fa-comments'"
                        (clicked)="openChatModal()"
                      />
                    </div>
                  }
                </div>
              </div>
              <!-- Social Links -->
              @if (hasSocialLinks()) {
                <div class="d-flex flex-wrap gap-2 mt-3">
                  @if (company.website_url) {
                    <a [href]="formatUrl(company.website_url)" target="_blank" rel="noopener noreferrer" class="social-link-sm social-website" title="Website">
                      <i class="fa-solid fa-globe"></i>
                    </a>
                  }
                  @if (company.facebook_url) {
                    <a [href]="formatUrl(company.facebook_url)" target="_blank" rel="noopener noreferrer" class="social-link-sm social-facebook" title="Facebook">
                      <i class="fa-brands fa-facebook-f"></i>
                    </a>
                  }
                  @if (company.instagram_url) {
                    <a [href]="formatUrl(company.instagram_url)" target="_blank" rel="noopener noreferrer" class="social-link-sm social-instagram" title="Instagram">
                      <i class="fa-brands fa-instagram"></i>
                    </a>
                  }
                  @if (company.youtube_url) {
                    <a [href]="formatUrl(company.youtube_url)" target="_blank" rel="noopener noreferrer" class="social-link-sm social-youtube" title="YouTube">
                      <i class="fa-brands fa-youtube"></i>
                    </a>
                  }
                  @if (company.tiktok_url) {
                    <a [href]="formatUrl(company.tiktok_url)" target="_blank" rel="noopener noreferrer" class="social-link-sm social-tiktok" title="TikTok">
                      <i class="fa-brands fa-tiktok"></i>
                    </a>
                  }
                  @if (company.twitter_url) {
                    <a [href]="formatUrl(company.twitter_url)" target="_blank" rel="noopener noreferrer" class="social-link-sm social-twitter" title="X (Twitter)">
                      <i class="fa-brands fa-x-twitter"></i>
                    </a>
                  }
                  @if (company.linkedin_url) {
                    <a [href]="formatUrl(company.linkedin_url)" target="_blank" rel="noopener noreferrer" class="social-link-sm social-linkedin" title="LinkedIn">
                      <i class="fa-brands fa-linkedin-in"></i>
                    </a>
                  }
                  @if (company.merchandise_url) {
                    <a [href]="formatUrl(company.merchandise_url)" target="_blank" rel="noopener noreferrer" class="social-link-sm social-merchandise" title="Merchandise">
                      <i class="fa-solid fa-shirt"></i>
                    </a>
                  }
                </div>
              }
            </div>
          </div>
        </div>
      </div>

      <!-- Content Section -->
      <div class="row g-6 mt-5">
        <!-- Left Column - About, Contact & Team -->
        <div class="col-lg-6">
          <!-- About -->
          <div class="card mb-6">
            <div class="card-header">
              <h3 class="card-title fw-bold">
                <i class="fa fa-info-circle text-primary me-2"></i>{{ 'organisationDetails.about' | translate }}
              </h3>
            </div>
            <div class="card-body">
              @if (company.description) {
                <div class="text-gray-700 organisation-description"
                     [class.collapsed]="hasLongDescription && !descriptionExpanded"
                     [innerHTML]="company.description"></div>
                @if (hasLongDescription) {
                  <div class="mt-3">
                    <a class="text-primary cursor-pointer fw-semibold" (click)="toggleDescription()">
                      {{ descriptionExpanded ? ('organisationDetails.showLess' | translate) : ('organisationDetails.readMore' | translate) }}
                      <i class="fa ms-1" [class.fa-chevron-up]="descriptionExpanded" [class.fa-chevron-down]="!descriptionExpanded"></i>
                    </a>
                  </div>
                }
              } @else {
                <p class="text-muted mb-0">{{ 'organisationDetails.noDescription' | translate }}</p>
              }
            </div>
          </div>

          <!-- Contact Information -->
          <div class="card mb-6">
            <div class="card-header">
              <h3 class="card-title fw-bold">
                <i class="fa fa-address-book text-primary me-2"></i>{{ 'organisationDetails.contactInfo' | translate }}
              </h3>
            </div>
            <div class="card-body">
              <div class="row g-4">
                @if (company.address) {
                  <div class="col-12">
                    <div class="d-flex">
                      <div class="symbol symbol-40px me-4">
                        <div class="symbol-label bg-light-primary">
                          <i class="fa fa-map-marker-alt text-primary"></i>
                        </div>
                      </div>
                      <div>
                        <div class="text-muted fs-7">{{ 'organisationDetails.address' | translate }}</div>
                        <a [href]="getGoogleMapsUrl()" target="_blank" class="text-gray-800 text-hover-primary fw-semibold">
                          {{ company.address }}
                          @if (company.postal_code || company.city) {
                            , {{ company.postal_code }} {{ company.city }}
                          }
                          @if (company.country) {
                            , {{ company.country }}
                          }
                        </a>
                      </div>
                    </div>
                  </div>
                }
                @if (company.email) {
                  <div class="col-md-6">
                    <div class="d-flex">
                      <div class="symbol symbol-40px me-4">
                        <div class="symbol-label bg-light-info">
                          <i class="fa fa-envelope text-info"></i>
                        </div>
                      </div>
                      <div>
                        <div class="text-muted fs-7">{{ 'organisationDetails.email' | translate }}</div>
                        <a [href]="'mailto:' + company.email" class="text-gray-800 text-hover-primary fw-semibold">
                          {{ company.email }}
                        </a>
                      </div>
                    </div>
                  </div>
                }
                @if (company.phone) {
                  <div class="col-md-6">
                    <div class="d-flex">
                      <div class="symbol symbol-40px me-4">
                        <div class="symbol-label bg-light-success">
                          <i class="fa fa-phone text-success"></i>
                        </div>
                      </div>
                      <div>
                        <div class="text-muted fs-7">{{ 'organisationDetails.phone' | translate }}</div>
                        <a [href]="'tel:' + company.phone" class="text-gray-800 text-hover-primary fw-semibold">
                          {{ company.phone }}
                        </a>
                      </div>
                    </div>
                  </div>
                }
              </div>

              <!-- Map -->
              @if (mapMarker) {
                <div class="mt-5">
                  <app-location-map
                    [markers]="[mapMarker]"
                    [config]="mapConfig"
                    [loading]="geocodingInProgress"
                  />
                </div>
              }
            </div>
          </div>

          <!-- Team -->
          <div class="card">
            <div class="card-header">
              <h3 class="card-title fw-bold">
                <i class="fa fa-users text-primary me-2"></i>{{ 'organisationDetails.team' | translate }}
              </h3>
            </div>
            <div class="card-body">
              @if (loadingTeam) {
                <div class="d-flex justify-content-center py-5">
                  <div class="spinner-border spinner-border-sm text-primary" role="status">
                    <span class="visually-hidden">{{ 'organisationDetails.loading' | translate }}</span>
                  </div>
                </div>
              } @else if (teamMembers.length === 0) {
                <div class="text-center py-5">
                  <p class="text-muted mb-0">{{ 'organisationDetails.noTeamMembers' | translate }}</p>
                </div>
              } @else {
                <div class="d-flex flex-wrap gap-4">
                  @for (member of teamMembers; track member.user_id) {
                    <div class="team-member">
                      <div class="team-avatar">
                        @if (member.profile_image_url) {
                          <img [src]="member.profile_image_url" [alt]="member.full_name || member.first_name"/>
                        } @else {
                          <span>{{ getMemberInitials(member) }}</span>
                        }
                      </div>
                      <div class="team-info">
                        <div class="team-name">{{ member.full_name || (member.first_name + ' ' + member.last_name) }}</div>
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          </div>

          <!-- Documents -->
          @if (loadingDocuments || documents.length > 0) {
            <div class="card mt-6">
              <div class="card-header">
                <h3 class="card-title fw-bold">
                  <i class="fa fa-file-alt text-primary me-2"></i>{{ 'organisationDetails.documents' | translate }}
                </h3>
              </div>
              <div class="card-body">
                @if (loadingDocuments) {
                  <div class="d-flex justify-content-center py-5">
                    <div class="spinner-border spinner-border-sm text-primary" role="status">
                      <span class="visually-hidden">{{ 'organisationDetails.loading' | translate }}</span>
                    </div>
                  </div>
                } @else {
                  <div class="documents-list">
                    @for (doc of documents; track doc.document_id) {
                      <div class="document-item" (click)="downloadDocument(doc)">
                        <div class="document-icon">
                          <i class="fa {{ getFileIcon(doc.file_extension) }} fs-2"></i>
                        </div>
                        <div class="document-info">
                          <div class="document-name">{{ doc.document_name }}</div>
                          @if (doc.file_size) {
                            <div class="document-meta">
                              <span class="document-size">{{ formatFileSize(doc.file_size) }}</span>
                            </div>
                          }
                        </div>
                        <div class="document-download">
                          <i class="fa fa-download"></i>
                        </div>
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          }

        </div>

        <!-- Right Column - Events -->
        <div class="col-lg-6">
          <!-- Events -->
          <div class="card">
            <div class="card-header">
              <h3 class="card-title fw-bold">
                <i class="fa fa-calendar text-primary me-2"></i>{{ 'organisationDetails.events' | translate }}
              </h3>
              <!-- Tabs -->
              <div class="card-toolbar">
                <ul class="nav nav-tabs nav-line-tabs nav-stretch fs-6 border-0">
                  <li class="nav-item">
                    <a class="nav-link" [class.active]="activeTab === 'upcoming'" (click)="setActiveTab('upcoming')">
                      {{ 'organisationDetails.upcoming' | translate }}
                      <span class="badge badge-light-success ms-2">{{ upcomingEvents.length }}</span>
                    </a>
                  </li>
                  <li class="nav-item">
                    <a class="nav-link" [class.active]="activeTab === 'past'" (click)="setActiveTab('past')">
                      {{ 'organisationDetails.past' | translate }}
                      <span class="badge badge-light-secondary ms-2">{{ pastEvents.length }}</span>
                    </a>
                  </li>
                  <li class="nav-item">
                    <a class="nav-link" [class.active]="activeTab === 'all'" (click)="setActiveTab('all')">
                      {{ 'organisationDetails.all' | translate }}
                      <span class="badge badge-light-primary ms-2">{{ events.length }}</span>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <div class="card-body">
              @if (loadingEvents) {
                <div class="d-flex flex-column align-items-center py-10">
                  <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">{{ 'organisationDetails.loading' | translate }}</span>
                  </div>
                </div>
              } @else if (getDisplayedEvents().length === 0) {
                <div class="d-flex flex-column align-items-center py-10">
                  <i class="fa fa-calendar-xmark fs-3x text-muted mb-4"></i>
                  <p class="text-muted mb-0">{{ 'organisationDetails.noEvents' | translate }}</p>
                </div>
              } @else {
                <div class="events-list">
                  @for (event of getDisplayedEvents(); track event.event_id) {
                    <div class="event-item">
                      <div class="row align-items-center">
                        <!-- Event Image -->
                        <div class="col-auto">
                          <div class="event-thumbnail">
                            @if (event.header_url) {
                              <img [src]="event.header_url" [alt]="event.event_name"/>
                            } @else {
                              <div class="thumbnail-placeholder">
                                <i class="fa fa-calendar"></i>
                              </div>
                            }
                          </div>
                        </div>

                        <!-- Event Info -->
                        <div class="col">
                          <h5 class="fw-bold mb-1">{{ event.event_name }}</h5>
                          <div class="d-flex flex-wrap gap-3 text-muted fs-7">
                            @if (event.start_date) {
                              <span>
                                <i class="fa fa-calendar me-1"></i>
                                {{ formatDateDMYDots(event.start_date) }}
                                @if (event.end_date && event.start_date !== event.end_date) {
                                  - {{ formatDateDMYDots(event.end_date) }}
                                }
                              </span>
                            }
                            @if (event.city) {
                              <span>
                                <i class="fa fa-location-dot me-1"></i>
                                {{ event.city }}
                              </span>
                            }
                          </div>
                        </div>

                        <!-- Action -->
                        <div class="col-auto">
                          <app-button
                            [variant]="'outline'"
                            [label]="'organisationDetails.view' | translate"
                            [routerLink]="'/event-details/' + event.event_id"
                          />
                        </div>
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          </div>

          <!-- Market Deals (only for stores_services) -->
          @if (company.company_type === 'stores_services' && (loadingDeals || deals.length > 0)) {
            <div class="card mt-6">
              <div class="card-header">
                <h3 class="card-title fw-bold">
                  <i class="fa fa-tag text-danger me-2"></i>{{ 'storesServices.deals' | translate }}
                </h3>
              </div>
              <div class="card-body">
                @if (loadingDeals) {
                  <div class="d-flex justify-content-center py-5">
                    <div class="spinner-border spinner-border-sm text-primary" role="status">
                      <span class="visually-hidden">{{ 'organisationDetails.loading' | translate }}</span>
                    </div>
                  </div>
                } @else {
                  <div class="row g-4">
                    @for (deal of deals; track deal.deal_id) {
                      <div class="col-12">
                        <app-deal-card
                          [deal]="deal"
                          [showCompanyInfo]="false"
                        />
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          }

          <!-- Feed Posts -->
          @if (loadingFeed || feedPosts.length > 0) {
            <div class="card mt-6">
              <div class="card-header">
                <h3 class="card-title fw-bold">
                  <i class="fa fa-newspaper text-primary me-2"></i>{{ 'organisationDetails.latestPosts' | translate }}
                </h3>
              </div>
              <div class="card-body">
                @if (loadingFeed) {
                  <div class="d-flex flex-column align-items-center py-10">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">{{ 'organisationDetails.loading' | translate }}</span>
                    </div>
                  </div>
                } @else {
                  <div class="feed-posts-list">
                    @for (post of feedPosts; track post.feed_post_id) {
                      <div class="feed-post-item">
                        <div class="feed-post-header">
                          <h5 class="fw-bold mb-1">{{ post.title }}</h5>
                          <span class="text-muted fs-7">{{ formatFeedDate(post.created_at) }}</span>
                        </div>
                        @if (post.description) {
                          <div class="feed-post-content text-gray-700 mt-2" [innerHTML]="post.description"></div>
                        }
                        @if (post.images && post.images.length > 0) {
                          <div class="feed-post-gallery mt-3">
                            @if (post.images.length === 1) {
                              <div class="feed-single-image">
                                <img [src]="post.images[0].image_url" alt="Post image" (click)="openLightbox(post, 0)" />
                              </div>
                            } @else if (post.images.length === 2) {
                              <div class="feed-two-images">
                                @for (img of post.images; track img.image_id; let i = $index) {
                                  <div class="feed-gallery-image">
                                    <img [src]="img.image_url" alt="Post image" (click)="openLightbox(post, i)" />
                                  </div>
                                }
                              </div>
                            } @else {
                              <div class="feed-multi-images">
                                @for (img of post.images; track img.image_id; let i = $index) {
                                  @if (i < 4) {
                                    <div class="feed-gallery-image" [class.feed-more-overlay]="i === 3 && post.images.length > 4">
                                      <img [src]="img.image_url" alt="Post image" (click)="openLightbox(post, i)" />
                                      @if (i === 3 && post.images.length > 4) {
                                        <div class="feed-more-count" (click)="openLightbox(post, 3)">
                                          +{{ post.images.length - 4 }}
                                        </div>
                                      }
                                    </div>
                                  }
                                }
                              </div>
                            }
                          </div>
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          }
        </div>
      </div>

      <!-- Gallery Section -->
      @if (loadingImages || companyImages.length > 0) {
        <div class="card mt-6 mb-6">
          <div class="card-header">
            <h3 class="card-title fw-bold">
              <i class="fa fa-images text-primary me-2"></i>{{ 'organisationDetails.gallery' | translate }}
            </h3>
          </div>
          <div class="card-body">
            @if (loadingImages) {
              <div class="d-flex justify-content-center py-5">
                <div class="spinner-border spinner-border-sm text-primary" role="status">
                  <span class="visually-hidden">{{ 'organisationDetails.loading' | translate }}</span>
                </div>
              </div>
            } @else {
              <div class="gallery-grid">
                @for (image of companyImages; track image.image_id; let i = $index) {
                  <div class="gallery-item" (click)="openImageLightbox(i)">
                    <img [src]="image.image_url" [alt]="'Gallery image ' + (i + 1)" />
                    <div class="gallery-overlay">
                      <i class="fa fa-expand"></i>
                    </div>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      }

      <!-- Sponsors Section -->
      @if (sponsors.length > 0) {
        <div class="sponsors-section">
          <h3>{{ 'organisationDetails.ourSponsors' | translate }}</h3>
          <div class="sponsors-marquee-container">
            <div class="sponsors-marquee">
              <div class="sponsors-track" [class.animate]="shouldAnimateSponsors()" [class.static]="!shouldAnimateSponsors()">
                @for (sponsor of sponsors; track sponsor.entry_id) {
                  @if (sponsor.sponsor) {
                    <div class="sponsor-slide">
                      <div class="sponsor-card-animated">
                        <div class="sponsor-badge">
                          @if (sponsor.sponsor.logo_url) {
                            <img [src]="sponsor.sponsor.logo_url"
                                 [alt]="sponsor.sponsor.sponsor_name"
                                 class="sponsor-logo-animated" />
                          } @else {
                            <div class="sponsor-logo-placeholder-animated">
                              <i class="fa fa-building"></i>
                            </div>
                          }
                          <div class="sponsor-shine"></div>
                        </div>
                        <div class="sponsor-details">
                          <h4>{{ sponsor.sponsor.sponsor_name }}</h4>
                          @if (sponsor.sponsor.sponsor_description) {
                            <p>{{ sponsor.sponsor.sponsor_description }}</p>
                          }
                        </div>
                      </div>
                    </div>
                  }
                }
                <!-- Duplicate for seamless loop (only when animating) -->
                @if (shouldAnimateSponsors()) {
                  @for (sponsor of sponsors; track sponsor.entry_id) {
                    @if (sponsor.sponsor) {
                      <div class="sponsor-slide">
                        <div class="sponsor-card-animated">
                          <div class="sponsor-badge">
                            @if (sponsor.sponsor.logo_url) {
                              <img [src]="sponsor.sponsor.logo_url"
                                   [alt]="sponsor.sponsor.sponsor_name"
                                   class="sponsor-logo-animated" />
                            } @else {
                              <div class="sponsor-logo-placeholder-animated">
                                <i class="fa fa-building"></i>
                              </div>
                            }
                            <div class="sponsor-shine"></div>
                          </div>
                          <div class="sponsor-details">
                            <h4>{{ sponsor.sponsor.sponsor_name }}</h4>
                            @if (sponsor.sponsor.sponsor_description) {
                              <p>{{ sponsor.sponsor.sponsor_description }}</p>
                            }
                          </div>
                        </div>
                      </div>
                    }
                  }
                }
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  }

  <!-- Lightbox -->
  @if (showLightbox) {
    <div class="feed-lightbox-overlay" (click)="closeLightbox()">
      <button class="feed-lightbox-close" (click)="closeLightbox()">
        <i class="fa fa-times"></i>
      </button>
      <button class="feed-lightbox-nav feed-lightbox-prev" (click)="previousImage(); $event.stopPropagation()">
        <i class="fa fa-chevron-left"></i>
      </button>
      <div class="feed-lightbox-content" (click)="$event.stopPropagation()">
        <img [src]="lightboxImages[lightboxIndex]" alt="Full size image" />
        <div class="feed-lightbox-counter">
          {{ lightboxIndex + 1 }} / {{ lightboxImages.length }}
        </div>
      </div>
      <button class="feed-lightbox-nav feed-lightbox-next" (click)="nextImage(); $event.stopPropagation()">
        <i class="fa fa-chevron-right"></i>
      </button>
    </div>
  }

  <!-- Not Found -->
  @if (!loading && !company) {
    <div class="container-xxl py-20">
      <div class="d-flex flex-column align-items-center">
        <i class="fa fa-building fs-3x text-muted mb-5"></i>
        <h3 class="text-muted">{{ 'organisationDetails.notFound' | translate }}</h3>
        <p class="text-muted mb-5">{{ 'organisationDetails.notFoundDesc' | translate }}</p>
        <app-button
          [variant]="'primary'"
          [label]="'organisationDetails.backToOrganisations' | translate"
          [routerLink]="'/organisations'"
        />
      </div>
    </div>
  }
</div>
